import { Contract, PAGES, PropositionProductTypeValues } from "@dune/types";
import { ContractStatus } from "@dune/types";
import { PaginatedDataTable, useSidebar } from "@dune/ui";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useContracts,
	useContractsCounts,
} from "../../api/queries/contracts.queries";
import PageLayoutWithTabs from "../../component/layout/PageLayoutWithTabs";
import { useContractFilters } from "../../hooks/contracts/useContractUtilities";
import { ContractFilters } from "./components/ContractsTabs";
import { contractsColumns } from "./components/contractsColumns";

const ContractsPage: React.FC = () => {
	const navigate = useNavigate();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const { filters, updateFilters } = useContractFilters();

	const [searchInput, setSearchInput] = useState(filters.search || "");

	const { data: contractsResponse, isLoading, error } = useContracts(filters);

	const { data: countsData } = useContractsCounts();

	const contracts = contractsResponse?.data || [];
	const productTypeCounts = countsData?.productTypeCounts || {};
	const statusCounts = countsData?.statusCounts || {};

	const paginationMeta = contractsResponse?.meta || {
		page: filters.page || 1,
		limit: filters.limit || 10,
		total: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const handlePageChange = (page: number) => {
		updateFilters({ page });
	};

	const handleLimitChange = (limit: number) => {
		updateFilters({ limit, page: 1 });
	};

	const handleSearchChange = (value: string) => {
		setSearchInput(value);
		updateFilters({ search: value || undefined, page: 1 });
	};

	const handleProductTypeFilter = (
		productType: PropositionProductTypeValues | undefined,
	) => {
		updateFilters({
			productType,
			page: 1,
		});
	};

	const handleStatusFilter = (
		status: keyof typeof ContractStatus | undefined,
	) => {
		updateFilters({
			status,
			page: 1,
		});
	};

	const handleRowClick = (contract: Contract) => {
		navigate(PAGES.CONTRACTS.show.withParams(contract.id.toString()));
	};

	const columns = contractsColumns();

	if (error) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="text-red-600">
					Erreur lors du chargement des contrats
				</div>
			</div>
		);
	}

	return (
		<PageLayoutWithTabs title="Contrats" tabContent={{}}>
			<div className="space-y-6 flex-1 min-h-0 flex flex-col">
				{/* Filters */}
				<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
					<div className="flex flex-col gap-4">
						<ContractFilters
							statusFilter={filters.status}
							onStatusFilterChange={handleStatusFilter}
							productTypeFilter={filters.productType}
							onProductTypeFilterChange={handleProductTypeFilter}
							statusCounts={statusCounts}
							productTypeCounts={productTypeCounts}
							searchInput={searchInput}
							onSearchChange={handleSearchChange}
						/>
					</div>
				</div>

				{/* Table */}
				<div className="flex-1 min-h-0 w-full overflow-auto">
					<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
						<PaginatedDataTable
							data={contracts}
							columns={columns}
							meta={paginationMeta}
							onPageChange={handlePageChange}
							onLimitChange={handleLimitChange}
							isLoading={isLoading}
							onRowClick={handleRowClick}
							noResultsText="Aucun contrat trouvé"
						/>
					</div>
				</div>
			</div>
		</PageLayoutWithTabs>
	);
};

export default ContractsPage;
